import type {
  BancoDoCatalogo,
  CertificadoDoPainel,
  Convenio,
  SituacaoDoCertificado,
} from "@/api/contrato";
import { FalhaDaApi } from "@/api/problema";
import type { Matiz } from "@/componentes/PilulaDeSituacao";
import { dataHora, vencimentoRelativo } from "@/formato";
import { usaWebService } from "./convenio";
import { errosDoFormulario, type ErrosDoFormulario } from "./recusas";

/*
 * O certificado na tela: quando o cartão aparece, o que ele diz e o formulário da troca.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O ARQUIVO E A SENHA SÓ EXISTEM NO ESTADO DO MODAL.                       │
 * │                                                                          │
 * │ Nada aqui guarda: as funções recebem os valores e devolvem o `FormData`  │
 * │ da viagem. O nome do arquivo do operador não sai do navegador: o `File`  │
 * │ vai com um nome fixo, e a API só precisa da extensão.                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A validação daqui poupa a viagem e espelha a API, sem substituí-la: quem decide é
 * `httpapi/multipart.go`, e o provider depois dele.
 */

/** O filtro do seletor de arquivo. */
export const EXTENSOES_DO_CERTIFICADO = ".pfx,.p12";

const TETO_DO_ARQUIVO_EM_BYTES = 128 * 1024;
const TETO_DA_SENHA = 20;
const TETO_DO_APELIDO = 250;

/** Em caracteres, como a API: a senha de 20 caracteres pode ter 25 bytes. */
function caracteres(texto: string): number {
  return Array.from(texto).length;
}

/**
 * Se o catálogo pede certificado neste convênio: o banco exige, e só no WebService. Sicoob em
 * modo manual não pede, e a pendência também não fala dele. Sem o catálogo carregado, não.
 */
export function exigeCertificado(linha: BancoDoCatalogo | undefined, convenio: Convenio): boolean {
  return linha?.exige_certificado === true && usaWebService(convenio.modo_registro);
}

/** O cartão aparece quando o catálogo exige, ou quando há linha. */
export function mostraCartaoDoCertificado(
  linha: BancoDoCatalogo | undefined,
  convenio: Convenio,
): boolean {
  return convenio.certificado.situacao !== "ausente" || exigeCertificado(linha, convenio);
}

const ROTULO_DA_SITUACAO: Record<SituacaoDoCertificado, string> = {
  ausente: "ausente",
  desconhecido: "desconhecido",
  valido: "válido",
  vencendo: "vencendo",
  vencido: "vencido",
};

/** A pílula só fora do normal. `normal` ainda fala ao leitor de tela, por `PilulaDeExcecao`. */
export function pilulaDoCertificado(situacao: SituacaoDoCertificado): {
  normal: boolean;
  matiz: Matiz;
  rotulo: string;
} {
  return {
    normal: situacao !== "vencendo" && situacao !== "vencido",
    matiz: situacao === "vencido" ? "falha" : "transito",
    rotulo: ROTULO_DA_SITUACAO[situacao],
  };
}

export function textoDoVencimento(certificado: CertificadoDoPainel): string {
  return certificado.situacao === "desconhecido"
    ? "Vencimento desconhecido"
    : vencimentoRelativo(certificado.vencimento);
}

/**
 * A data exata, na `Dica`. No convênio, `nivel: cedente` diz de onde veio o vencimento: vale o
 * menor dos dois, e a precedência entre eles não é documentada pelo provider.
 */
export function dicaDoVencimento(certificado: CertificadoDoPainel, noConvenio: boolean): string {
  if (certificado.situacao === "desconhecido") return "A TecnoSpeed não informou o vencimento.";

  const data = `Vencimento em ${dataHora(certificado.vencimento ?? undefined)}.`;
  return noConvenio && certificado.nivel === "cedente"
    ? `${data} É o do certificado do cedente, que vence antes.`
    : data;
}

export interface ValoresDoEnvio {
  arquivo: File | null;
  senha: string;
  apelido: string;
}

export type CampoDoEnvio = "arquivo" | "senha" | "apelido";
export type ErrosDoEnvio = Partial<Record<CampoDoEnvio, string>>;

const CAMPOS_DO_ENVIO: readonly CampoDoEnvio[] = ["arquivo", "senha", "apelido"];

function extensaoDe(arquivo: File): ".pfx" | ".p12" | null {
  const nome = arquivo.name.toLowerCase();
  if (nome.endsWith(".pfx")) return ".pfx";
  return nome.endsWith(".p12") ? ".p12" : null;
}

/** A recusa do arquivo, na hora da escolha: sem viagem nenhuma. */
export function erroDoArquivo(arquivo: File): string | undefined {
  if (extensaoDe(arquivo) === null) return "Escolha um arquivo .pfx ou .p12.";
  if (arquivo.size === 0) return "O arquivo está vazio.";
  return arquivo.size > TETO_DO_ARQUIVO_EM_BYTES ? "O arquivo passa de 128 KiB." : undefined;
}

/** Vazio é o formulário pronto para enviar. A senha é conferida como veio, sem aparar. */
export function validarEnvio({ arquivo, senha, apelido }: ValoresDoEnvio): ErrosDoEnvio {
  const erros: ErrosDoEnvio = {};

  if (arquivo === null) erros.arquivo = "Escolha o arquivo do certificado.";
  if (senha === "") erros.senha = "Informe a senha do certificado.";
  else if (caracteres(senha) > TETO_DA_SENHA) erros.senha = "A senha tem até 20 caracteres.";
  if (caracteres(apelido.trim()) > TETO_DO_APELIDO) {
    erros.apelido = "O apelido tem até 250 caracteres.";
  }

  return erros;
}

/** O corpo da viagem. O apelido vazio não vai: a API usa o número do convênio. */
export function formularioDoEnvio(arquivo: File, senha: string, apelido: string): FormData {
  const formulario = new FormData();
  formulario.append("arquivo", arquivo, `certificado${extensaoDe(arquivo) ?? ".pfx"}`);
  formulario.append("senha", senha);
  if (apelido.trim() !== "") formulario.append("apelido", apelido.trim());
  return formulario;
}

const ENVIO_INCERTO = "A TecnoSpeed não respondeu, e o envio pode ter chegado. Confira antes de repetir.";

/** Sem `Retry-After`, a recusa é a credencial do motor: nada chegou, e o texto é outro. */
function envioPodeTerChegado(falha: unknown): boolean {
  if (!(falha instanceof FalhaDaApi)) return false;
  if (falha.codigo === "provider_resposta_invalida") return true;
  return falha.codigo === "provider_indisponivel" && falha.tenteEmSegundos !== null;
}

/** A recusa do envio: no campo, quando ela aponta um, ou na faixa. */
export function errosDoEnvio(falha: unknown): ErrosDoFormulario<CampoDoEnvio> {
  if (envioPodeTerChegado(falha)) return { porCampo: {}, geral: ENVIO_INCERTO };
  if (falha instanceof FalhaDaApi && falha.codigo === "payload_too_large") {
    return { porCampo: { arquivo: "O arquivo passa de 128 KiB." }, geral: undefined };
  }
  return errosDoFormulario(falha, CAMPOS_DO_ENVIO);
}
